import type { DiscoveryFacts, PlaybookFacts, LevelCode, Evidence } from '@/api/types/readiness'

// ── Signals as configuration (§3.2, Engineering Note 3) ─────────────────────
// A rule table: (objective_code, level, signal_key, params). The evaluator
// proposes the highest level for which every rule at that level is satisfied
// (L1 rows are "any", L2 rows are "all"), capped at SUGGESTION_CAP. L3 is only
// ever declared by a user.

export const SUGGESTION_CAP: LevelCode = 'L2'

export type SignalKey =
  | 'discovery.inventory_exists'
  | 'discovery.prioritised_inventory_with_cbom'
  | 'discovery.cert_key_inventory'
  | 'discovery.vendor_component'
  | 'playbook.plan_any'
  | 'playbook.plan_evaluated_for_prioritised'
  // Module 3 (I7): the Issuing CA register and the NIST remediation report
  // qtz-readiness serves under /playbook/*.
  | 'playbook.issuing_ca_any'
  | 'playbook.replacement_identified'
  | 'evidence.vendor_assessment'
  | 'manual_only'

export interface SignalRule {
  objective_code: string
  level: 'L1' | 'L2'
  signal_key: SignalKey
  params?: Record<string, unknown>
}

export const SIGNAL_RULES: SignalRule[] = [
  { objective_code: 'RA-1', level: 'L1', signal_key: 'discovery.inventory_exists', params: { min: 1 } },
  { objective_code: 'RA-1', level: 'L2', signal_key: 'discovery.prioritised_inventory_with_cbom', params: { min: 1 } },
  { objective_code: 'RA-2', level: 'L1', signal_key: 'manual_only' },
  { objective_code: 'GV-1', level: 'L1', signal_key: 'manual_only' },
  { objective_code: 'GV-2', level: 'L1', signal_key: 'manual_only' },
  { objective_code: 'TE-1', level: 'L1', signal_key: 'playbook.plan_any', params: { min: 1 } },
  { objective_code: 'TE-1', level: 'L1', signal_key: 'playbook.replacement_identified', params: { min: 1 } },
  { objective_code: 'TE-1', level: 'L2', signal_key: 'playbook.plan_evaluated_for_prioritised', params: { min: 1 } },
  { objective_code: 'TE-2', level: 'L1', signal_key: 'discovery.cert_key_inventory' },
  { objective_code: 'TC-1', level: 'L1', signal_key: 'manual_only' },
  { objective_code: 'TC-2', level: 'L1', signal_key: 'manual_only' },
  { objective_code: 'EE-1', level: 'L1', signal_key: 'discovery.vendor_component', params: { min: 1 } },
  { objective_code: 'EE-1', level: 'L1', signal_key: 'playbook.issuing_ca_any', params: { min: 1 } },
  { objective_code: 'EE-1', level: 'L2', signal_key: 'evidence.vendor_assessment', params: { min: 1 } },
  { objective_code: 'EE-2', level: 'L1', signal_key: 'manual_only' },
]

export interface SignalContext {
  discovery: DiscoveryFacts
  playbook: PlaybookFacts
  evidence: Evidence[] // existing (non-archived) evidence for the index
  assessments?: number // saved assessments in this scope (QPI PP-1, I2 §11.3)
}

export interface SignalResult {
  met: boolean
  detail: string
}

function minOf(params?: Record<string, unknown>): number {
  const v = params?.min
  return typeof v === 'number' ? v : 1
}

export function evaluateSignal(rule: SignalRule, ctx: SignalContext): SignalResult {
  const { discovery, playbook, evidence } = ctx
  const min = minOf(rule.params)
  switch (rule.signal_key) {
    case 'manual_only':
      return { met: false, detail: 'Manual input only: this objective is set by a user, not suggested by sync.' }
    case 'discovery.inventory_exists': {
      if (!discovery.connected) return { met: false, detail: 'Discovery not connected.' }
      const n = discovery.inventories.length
      return { met: n >= min, detail: `${n} Discovery ${n === 1 ? 'inventory' : 'inventories'} present (need ≥${min}).` }
    }
    case 'discovery.prioritised_inventory_with_cbom': {
      if (!discovery.connected) return { met: false, detail: 'Discovery not connected.' }
      const pri = discovery.inventories.filter((i) => i.prioritised)
      const withCbom = pri.filter((i) => discovery.cbom_inventory_ids.includes(i.id))
      if (pri.length === 0)
        return { met: false, detail: 'No inventory is tagged as prioritised / CII, so organisation-wide prioritisation cannot be shown.' }
      const allHave = withCbom.length === pri.length && pri.length >= min
      return {
        met: allHave,
        detail: `${pri.length} prioritised ${pri.length === 1 ? 'inventory' : 'inventories'}, ${withCbom.length} with a CBOM (every prioritised inventory needs one).`,
      }
    }
    case 'discovery.cert_key_inventory': {
      if (!discovery.connected) return { met: false, detail: 'Discovery not connected.' }
      return {
        met: discovery.cert_key_inventory,
        detail: discovery.cert_key_inventory
          ? 'A certificate / key inventory is present in Discovery.'
          : 'No certificate / key inventory found in Discovery.',
      }
    }
    case 'discovery.vendor_component': {
      if (!discovery.connected) return { met: false, detail: 'Discovery not connected.' }
      const n = discovery.vendor_components.length
      return {
        met: n >= min,
        detail: `${n} vendor-supplied crypto ${n === 1 ? 'component' : 'components'} (HSM, CA, KMS) identified by Discovery.`,
      }
    }
    case 'playbook.plan_any': {
      if (!playbook.connected) return { met: false, detail: 'Playbook not connected.' }
      const n = playbook.plans.length
      return { met: n >= min, detail: `${n} Playbook ${n === 1 ? 'plan' : 'plans'} in any state.` }
    }
    case 'playbook.plan_evaluated_for_prioritised': {
      if (!playbook.connected) return { met: false, detail: 'Playbook not connected.' }
      const pri = new Set(discovery.inventories.filter((i) => i.prioritised).map((i) => i.id))
      const n = playbook.plans.filter(
        (p) => p.inventory_id && pri.has(p.inventory_id) && p.status !== 'draft'
      ).length
      return {
        met: n >= min,
        detail: `${n} Playbook ${n === 1 ? 'plan' : 'plans'} with a PQC replacement evaluated for a prioritised system.`,
      }
    }
    case 'playbook.issuing_ca_any': {
      if (!playbook.service_connected) return { met: false, detail: 'Playbook not connected.' }
      const n = playbook.issuing_cas.length
      return { met: n >= min, detail: `${n} Issuing ${n === 1 ? 'CA' : 'CAs'} (third-party certificate dependencies) identified by Playbook.` }
    }
    case 'playbook.replacement_identified': {
      if (!playbook.service_connected) return { met: false, detail: 'Playbook not connected.' }
      const n = playbook.remediations.filter((r) => r.vulnerable_count > 0).length
      return {
        met: n >= min,
        detail: `${n} quantum-safe replacement ${n === 1 ? 'option' : 'options'} identified by Playbook${playbook.standard ? ` (${playbook.standard})` : ''} for vulnerable assets.`,
      }
    }
    case 'evidence.vendor_assessment': {
      const n = evidence.filter(
        (e) =>
          e.objective_code === rule.objective_code &&
          !e.archived_at &&
          /vendor assessment/i.test(`${e.title} ${e.summary}`)
      ).length
      return { met: n >= min, detail: `${n} vendor ${n === 1 ? 'assessment' : 'assessments'} recorded as evidence.` }
    }
  }
}

export interface Suggestion {
  objective_code: string
  level: LevelCode | null // null = manual only / nothing met
  reason: string
  manual_only: boolean
  details: Array<{ level: 'L1' | 'L2'; met: boolean; detail: string }>
}

export function suggestLevel(objective_code: string, ctx: SignalContext): Suggestion {
  const rules = SIGNAL_RULES.filter((r) => r.objective_code === objective_code)
  const manualOnly = rules.length === 0 || rules.every((r) => r.signal_key === 'manual_only')
  if (manualOnly) {
    return { objective_code, level: null, reason: 'Manual input only.', manual_only: true, details: [] }
  }
  const details: Suggestion['details'] = []
  let level: LevelCode | null = null
  for (const lvl of ['L1', 'L2'] as const) {
    const lvlRules = rules.filter((r) => r.level === lvl && r.signal_key !== 'manual_only')
    if (lvlRules.length === 0) break
    const results = lvlRules.map((r) => evaluateSignal(r, ctx))
    results.forEach((r) => details.push({ level: lvl, met: r.met, detail: r.detail }))
    // L1 rows are "any"; L2 rows are "all" (§3.2).
    const ok = lvl === 'L1' ? results.some((r) => r.met) : results.every((r) => r.met)
    if (!ok) break
    level = lvl
    if (lvl === SUGGESTION_CAP) break
  }
  const met = details.filter((d) => d.met).map((d) => d.detail)
  const notMet = details.filter((d) => !d.met).map((d) => d.detail)
  const reason = level
    ? `${met.join(' ')} ${notMet.length ? `Next level not met: ${notMet.join(' ')}` : `Suggestions are capped at ${SUGGESTION_CAP}; L3 is declared by a user.`}`.trim()
    : `No signal met: ${notMet.join(' ')}`
  return { objective_code, level, reason, manual_only: false, details }
}

// ── HKMA QPI signals (Iteration 2 §11.3) ────────────────────────────────────
// Same pattern, keyed on tier instead of level: a rule met at tier n proposes
// tier n (capped at tier 2 of m). Sub-indices without a rule are manual only.

export type QpiSignalKey =
  | 'discovery.inventory_exists' // PL-4: inventories and CBOM presence
  | 'playbook.plan_any' // PL-3: plans exist
  | 'playbook.pilot_any' // PI-1: a plan evaluated / in progress (pilot)
  | 'readiness.assessment_saved' // PP-1: monitoring via saved assessments
  | 'discovery.hybrid_present' // PP-2: hybrid / PQC controls observed

export interface QpiSignalRule {
  objective_code: string
  tier: number
  signal_key: QpiSignalKey
}

export const QPI_SIGNAL_RULES: QpiSignalRule[] = [
  { objective_code: 'PL-4', tier: 2, signal_key: 'discovery.inventory_exists' },
  { objective_code: 'PL-3', tier: 2, signal_key: 'playbook.plan_any' },
  { objective_code: 'PI-1', tier: 2, signal_key: 'playbook.pilot_any' },
  { objective_code: 'PP-1', tier: 2, signal_key: 'readiness.assessment_saved' },
  { objective_code: 'PP-2', tier: 2, signal_key: 'discovery.hybrid_present' },
]

export function evaluateQpiSignal(rule: QpiSignalRule, ctx: SignalContext): SignalResult {
  const { discovery, playbook } = ctx
  switch (rule.signal_key) {
    case 'discovery.inventory_exists': {
      if (!discovery.connected) return { met: false, detail: 'Discovery not connected.' }
      const n = discovery.inventories.length
      const cbom = discovery.cbom_inventory_ids.length
      return { met: n >= 1, detail: `${n} Discovery ${n === 1 ? 'inventory' : 'inventories'}, ${cbom} with a CBOM.` }
    }
    case 'playbook.plan_any': {
      if (!playbook.connected) return { met: false, detail: 'Playbook not connected.' }
      const n = playbook.plans.length
      return { met: n >= 1, detail: `${n} Playbook ${n === 1 ? 'plan' : 'plans'} recorded.` }
    }
    case 'playbook.pilot_any': {
      if (!playbook.connected) return { met: false, detail: 'Playbook not connected.' }
      const n = playbook.plans.filter((p) => p.status !== 'draft').length
      return { met: n >= 1, detail: `${n} Playbook ${n === 1 ? 'plan' : 'plans'} evaluated or in progress (migration testing).` }
    }
    case 'readiness.assessment_saved': {
      const n = ctx.assessments ?? 0
      return { met: n >= 1, detail: `${n} saved ${n === 1 ? 'assessment' : 'assessments'} in Quantizant Readiness.` }
    }
    case 'discovery.hybrid_present': {
      if (!discovery.connected || !discovery.quantum_risk) return { met: false, detail: 'Discovery quantum-risk counts not available.' }
      const { hybrid, vulnerable } = discovery.quantum_risk
      return { met: hybrid > 0, detail: `${hybrid.toLocaleString()} hybrid / PQC assets against ${vulnerable.toLocaleString()} vulnerable in Discovery.` }
    }
  }
}

export interface TierSuggestion {
  objective_code: string
  tier: number | null
  reason: string
  manual_only: boolean
}

export const QPI_TIER_CAP = 2

export function suggestTier(objective_code: string, ctx: SignalContext): TierSuggestion {
  const rules = QPI_SIGNAL_RULES.filter((r) => r.objective_code === objective_code)
  if (rules.length === 0) return { objective_code, tier: null, reason: 'Manual input only.', manual_only: true }
  let tier: number | null = null
  const met: string[] = []
  const notMet: string[] = []
  rules
    .sort((a, b) => a.tier - b.tier)
    .forEach((r) => {
      const res = evaluateQpiSignal(r, ctx)
      if (res.met && r.tier <= QPI_TIER_CAP && (tier == null || r.tier > tier)) tier = r.tier
      ;(res.met ? met : notMet).push(res.detail)
    })
  const reason = tier != null ? `${met.join(' ')} Suggestions are capped at tier ${QPI_TIER_CAP}; higher tiers are declared by a user.` : `No signal met: ${notMet.join(' ')}`
  return { objective_code, tier, reason, manual_only: false }
}
